import { useIntl } from 'react-intl';
import { GridFilterItem } from '@rossum/ui/x-data-grid-pro';
import {
  FilterColDef,
  ValidatedGridFilterItem,
} from '../../document-list-filtering/types';
import { createNewFilterQuery } from '../../mql/transformers';
import { SavedViewsList } from '../../saved-views';
import { SAVED_VIEW_QUERY_NAME } from '../../saved-views/core/constants';
import { DashboardQuery } from '../hooks/useDashboardQuery';
import { STATUS_TABS_HEIGHT } from './tabs';

type SavedViewsTabProps = {
  handleQueryChange: (newQuery: DashboardQuery) => void;
  columns: FilterColDef[];
  activeQueueId: number;
  existingFilterWithoutQueue: ValidatedGridFilterItem[];
  savedViewId: string | undefined;
  isActive: boolean;
};

export const SavedViewsTab = ({
  handleQueryChange,
  columns,
  activeQueueId,
  existingFilterWithoutQueue,
  savedViewId,
  isActive,
}: SavedViewsTabProps) => {
  const intl = useIntl();

  const handleClickOnSavedView = ({
    viewId,
    filterItems,
  }: {
    viewId: string;
    filterItems: GridFilterItem[];
  }) => {
    const filtersWithQueue = filterItems.concat({
      field: 'queue',
      operator: 'isAnyOf',
      value: [activeQueueId],
    });

    handleQueryChange({
      filtering: createNewFilterQuery(filtersWithQueue),
      [SAVED_VIEW_QUERY_NAME]: viewId,
    });
  };

  // "All" is not a saved view, it is the absence of one plus a filter covering
  // all statuses; clearing a view keeps the filters, so it lands on "All"
  // unless they match a status tab.
  const isAllViewActive = isActive && !savedViewId;

  const handleSelectAllView = () => {
    const filtersWithoutStatus: GridFilterItem[] =
      existingFilterWithoutQueue.filter(item => item.field !== 'status');
    const filtersWithQueue = filtersWithoutStatus.concat({
      field: 'queue',
      operator: 'isAnyOf',
      value: [activeQueueId],
    });

    handleQueryChange({
      filtering: createNewFilterQuery(filtersWithQueue),
      [SAVED_VIEW_QUERY_NAME]: undefined,
    });
  };

  return (
    <SavedViewsList
      columns={columns}
      currentViewConfig={{
        filtering: existingFilterWithoutQueue,
        viewId: savedViewId,
      }}
      onMenuItemClick={handleClickOnSavedView}
      onMenuItemDelete={viewId => {
        if (viewId === savedViewId)
          handleQueryChange({
            [SAVED_VIEW_QUERY_NAME]: undefined,
          });
      }}
      onClearSelection={() =>
        handleQueryChange({ [SAVED_VIEW_QUERY_NAME]: undefined })
      }
      activeQueueId={activeQueueId}
      systemView={{
        label: intl.formatMessage({
          id: 'containers.allDocuments.statusTabs.allStatuses',
        }),
        isActive: isAllViewActive,
        onSelect: handleSelectAllView,
      }}
      buttonProps={{
        variant: 'text',
        sx: {
          height: STATUS_TABS_HEIGHT,
          minHeight: STATUS_TABS_HEIGHT,
          borderRadius: 0,
          maxWidth: 350,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          // mimics the status tabs' indicator (MUI default: 2px, primary,
          // transitions.create()), entering and exiting from the right
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 2,
            backgroundColor: 'primary.main',
            // due to dynamic size changes based on view name its better to just hide the underline when deactivating
            // otherwise it feels flickery
            visibility: isActive ? 'visible' : 'hidden',
            transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
            transformOrigin: 'right',
            transition: t =>
              t.transitions.create('transform', {
                duration: t.transitions.duration.shortest,
                delay: t.transitions.duration.short,
              }),
          },
        },
      }}
    />
  );
};
